import { motion } from 'framer-motion'

/**
 * About Component
 * Our Story, Values sections with image and updated background pattern
 */
const About = () => {
  const values = [
    {
      title: 'Clarity Across Cultures',
      description: 'We deliver multilingual packaging that communicates accurately — Arabic, Hindi, French, Spanish, and more.',
    },
    {
      title: 'Consistency at Scale',
      description: 'From a single SKU to a global rollout, we maintain visual harmony across formats, regions, and vendors.',
    },
    {
      title: 'Collaboration First',
      description: 'We work closely with regulatory, marketing, and production teams to ensure every file is compliant, aligned, and print ready.',
    },
    {
      title: 'Integrity in Execution',
      description: 'No shortcuts. No compromises. Only packaging that complies, communicates, and supports commercial success.',
    },
  ]

  return (
    <section
      id="about"
      className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-white border-t border-gray-200/50"
      aria-labelledby="about-heading"
    >
      {/* Updated background pattern */}
      <div 
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 2px, #662d91 2px, #662d91 4px),
            repeating-linear-gradient(90deg, transparent, transparent 2px, #662d91 2px, #662d91 4px)
          `,
          backgroundSize: '40px 40px'
        }}
      ></div>
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-primary-50/30 to-white"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2
            id="about-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight"
          >
            About Artwork Solution
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto"></div>
        </motion.div>

        {/* Our Story with Image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-24"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-2 lg:order-1"
            >
              <div className="relative">
                {/* Decorative background */}
                <div className="absolute -inset-4 bg-gradient-to-br from-primary-200 to-primary-400 rounded-3xl transform rotate-3 opacity-20"></div>
                {/* Image container */}
                <div className="relative bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-2 aspect-square flex items-center justify-center shadow-2xl overflow-hidden group">
                  <img 
                    src="/images/about.jpeg" 
                    alt="Artwork Solution team" 
                    className="w-full h-full object-cover rounded-2xl transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center absolute inset-0" style={{display: 'none'}}>
                    <svg className="w-32 h-32 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-primary-900/0 group-hover:bg-primary-900/20 transition-all duration-500 rounded-2xl"></div>
                </div>
              </div>
            </motion.div>

            {/* Content Side */}
            <div className="order-1 lg:order-2">
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-10 lg:p-12 shadow-xl border border-gray-100">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-4">
                  <div className="w-2 h-12 bg-gradient-to-b from-primary-500 to-primary-700 rounded-full"></div>
                  <span>Our Story</span>
                </h3>
                <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                  <p>
                    Artwork Solution was built on a clear belief: packaging is not just a technical requirement — it is a strategic asset.
                  </p>
                  <p className="text-gray-600">
                    With foundations in visual communication and a deep commitment to regulatory accuracy, we set out to close the gap between creative design and global compliance.
                  </p>
                  <p>
                    Today, we serve pharmaceutical, food, FMCG, and international markets, helping brands navigate the complexity of packaging artwork with clarity, consistency, and control.
                  </p>
                  <p className="font-medium text-gray-900">
                    Our work spans regions, languages, and regulatory systems — and our focus remains constant: transforming packaging into a reliable tool for trust, communication, and market performance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Our Values */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-12 text-center">
            Our Values
          </h3>

          {/* Precision with Purpose - Featured */}
          <div className="max-w-4xl mx-auto mb-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 rounded-3xl p-10 lg:p-12 text-white shadow-2xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
              <div className="relative z-10">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-3xl mb-4">Precision with Purpose</h4>
                    <p className="text-lg text-primary-50 leading-relaxed">
                      Every label, panel, and layout is crafted with regulatory rigor and clear brand intent.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Other Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
                className="group bg-white/90 backdrop-blur-sm rounded-2xl p-8 border-2 border-gray-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="flex items-start gap-5 relative z-10">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 group-hover:bg-primary-200 transition-all duration-300">
                    <span className="text-primary font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-xl text-gray-900 mb-3 group-hover:text-primary transition-colors duration-300">{value.title}</h4>
                    <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">{value.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      
      {/* Bottom decorative divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent"></div>
    </section>
  )
}

export default About
